import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Field } from "@/pages/Login";

export default function Activate() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | valid | invalid | done
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // Fase 7 — aceites obrigatórios
  const [adultConfirm, setAdultConfirm] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    (async () => {
      try {
        const { data } = await api.get(`/auth/activate/validate`, { params: { token } });
        setInfo(data);
        setStatus("valid");
      } catch (e) {
        setStatus("invalid");
      }
    })();
  }, [token]);

  const canSubmit = adultConfirm && termsAccepted && privacyAccepted && !loading;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("A senha deve ter pelo menos 8 caracteres.");
    if (pw !== pw2) return setErr("As senhas não coincidem.");
    if (!adultConfirm || !termsAccepted || !privacyAccepted) {
      return setErr("Confirme a idade responsável e aceite os Termos e a Política de Privacidade.");
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/activate", {
        token, password: pw,
        adult_confirmation: true,
        terms_accepted: true,
        privacy_accepted: true,
        acceptance_version: "2026-09",
      });
      if (data.token) localStorage.setItem("cdf_token", data.token);
      toast.success("Conta ativada com sucesso 💛");
      setStatus("done");
      setTimeout(() => navigate("/app"), 800);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10" data-testid="activate-page">
      <div className="w-full max-w-lg card-soft p-8">
        <div className="text-center">
          <div className="text-4xl">💛</div>
          <h1 className="mt-2 text-2xl font-extrabold text-[#243B53]">Ativação da conta</h1>
          <p className="text-sm text-[#243B53]/70">Defina uma senha e confirme os aceites para acessar o Clubinho.</p>
        </div>

        {status === "checking" && (
          <div className="mt-6 text-center text-[#243B53]/70" data-testid="activate-checking">Validando link…</div>
        )}

        {status === "invalid" && (
          <div className="mt-6 text-center" data-testid="activate-invalid">
            <p className="text-red-600 font-bold">Link inválido, já utilizado ou expirado.</p>
            <p className="mt-2 text-sm text-[#243B53]/70">Se sua compra foi confirmada, você pode solicitar um novo link à equipe.</p>
            <Link to="/login" className="btn-outline inline-block mt-4">Voltar ao login</Link>
          </div>
        )}

        {status === "valid" && (
          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="activate-form">
            <div className="text-sm text-[#243B53]/80">
              Conta: <b data-testid="activate-email">{info?.email_masked}</b>
            </div>
            <Field label="Nova senha" type="password" value={pw} onChange={setPw} testid="activate-password-input" />
            <Field label="Confirmar senha" type="password" value={pw2} onChange={setPw2} testid="activate-confirm-input" />

            <div className="space-y-3 pt-2 border-t border-[#243B53]/10">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="activate-adult-checkbox"
                  checked={adultConfirm}
                  onChange={(e) => setAdultConfirm(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded accent-[#D9A441]"
                />
                <span className="text-sm text-[#243B53]/85">
                  Declaro que tenho 18 anos ou mais e sou responsável pela conta do Clubinho da Fé.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="activate-terms-checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded accent-[#D9A441]"
                />
                <span className="text-sm text-[#243B53]/85">
                  Li e aceito os{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-[#5DADE2] font-bold" data-testid="activate-terms-link">
                    Termos de Uso
                  </Link>.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="activate-privacy-checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded accent-[#D9A441]"
                />
                <span className="text-sm text-[#243B53]/85">
                  Li e aceito a{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#5DADE2] font-bold" data-testid="activate-privacy-link">
                    Política de Privacidade
                  </Link>.
                </span>
              </label>
            </div>

            {err && <div data-testid="activate-error" className="text-sm text-red-600 font-bold">{err}</div>}
            <button
              data-testid="activate-submit-btn"
              disabled={!canSubmit}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Ativando…" : "Ativar conta"}
            </button>
          </form>
        )}

        {status === "done" && (
          <div className="mt-6 text-center text-[#243B53]" data-testid="activate-done">
            Conta ativada. Redirecionando…
          </div>
        )}
      </div>
    </div>
  );
}
